import { faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type React from 'react';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { WBSButton } from '@/common/WBSButton';
import { CheckboxLabel, WBSCheckbox } from '@/common/WBSCheckbox';
import { SearchSelect } from '~/styles/forms/SearchSelect';
import { SelectGroup } from '~/styles/forms/styledSelectGroup';
import {
  ButtonGroup,
  DateCol,
  DateRow,
  type FilterField,
  FilterGrid,
  FilterRow,
  Label,
  Panel,
  PanelContent,
  TextInput,
} from '~/styles/tables/filterandSettingsStyles';

export function SelectInput({
  value,
  onChange,
  children,
  ...props
}: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <SelectGroup bgColor="var(--white)">
      <select value={value} onChange={onChange} {...props}>
        {children}
      </select>
      <span className="icon">
        <FontAwesomeIcon icon={faCaretDown} />
      </span>
    </SelectGroup>
  );
}

export interface TableFilterPanelProps {
  isOpen: boolean;
  filters: FilterField[];
  values: Record<string, any>;
  onChange: (key: string, value: any) => void;
  onApply: () => void;
  onReset: () => void;
  rememberChecked?: boolean;
  onRememberChange?: (checked: boolean) => void;
  hideRememberFilterCheckbox?: boolean;
}

function TableFilterPanelComponent({
  isOpen,
  filters,
  values,
  onChange,
  onApply,
  onReset,
  rememberChecked = true,
  onRememberChange,
  hideRememberFilterCheckbox = false,
}: TableFilterPanelProps) {
  const { t } = useTranslation();
  const inputRefs = useRef<Record<string, HTMLInputElement>>({});
  const contentRef = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState('0px');

  // Sync external values to internal inputs when values prop changes
  useEffect(() => {
    Object.keys(inputRefs.current).forEach((key) => {
      const input = inputRefs.current[key];
      if (input && input.value !== (values[key] ?? '')) {
        input.value = values[key] ?? '';
      }
    });
  }, [values]);

  const handleInputChange = (key: string, value: string) => {
    // Only update parent on blur
    if (inputRefs.current[key]) {
      inputRefs.current[key].value = value;
    }
  };

  const handleInputBlur = (key: string) => {
    // Sync to parent when focus leaves the input
    const input = inputRefs.current[key];
    if (input) {
      onChange(key, input.value);
    }
  };

  const handleApplyClick = () => {
    // Sync all input values to parent before applying
    Object.keys(inputRefs.current).forEach((key) => {
      const input = inputRefs.current[key];
      if (input) {
        onChange(key, input.value);
      }
    });
    onApply();
  };

  const handleResetClick = () => {
    // Clear all inputs and sync to parent
    Object.keys(inputRefs.current).forEach((key) => {
      const input = inputRefs.current[key];
      if (input) {
        input.value = '';
        onChange(key, '');
      }
    });
    onReset();
  };
  const groupedFilters: (FilterField | FilterField[])[] = useMemo(() => {
    const result: (FilterField | FilterField[])[] = [];
    const processedKeys = new Set<string>();

    filters.forEach((field) => {
      if (processedKeys.has(field.key)) return;

      if (field.groupWith) {
        const groupedField = filters.find((f) => f.key === field.groupWith);
        if (groupedField && !processedKeys.has(groupedField.key)) {
          result.push([field, groupedField]);
          processedKeys.add(field.key);
          processedKeys.add(groupedField.key);
        } else {
          result.push(field);
          processedKeys.add(field.key);
        }
      } else {
        result.push(field);
        processedKeys.add(field.key);
      }
    });

    return result;
  }, [filters]);

  useEffect(() => {
    const content = contentRef.current;
    if (!isOpen || !content) {
      setMaxHeight('0px');
      return;
    }
    // Re-measure whenever the content reflows — e.g. crossing the mobile
    // breakpoint restacks the filter rows and changes the panel's height.
    const measure = () => setMaxHeight(`${content.scrollHeight}px`);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(content);
    return () => observer.disconnect();
  }, [isOpen]);

  const renderFieldInput = (field: FilterField) => {
    const fieldKey = field.key;

    const setTextInputRef = (element: HTMLInputElement | null) => {
      if (element) inputRefs.current[fieldKey] = element;
    };

    switch (field.type) {
      case 'text':
        return (
          <TextInput
            ref={setTextInputRef}
            defaultValue={values[fieldKey] ?? ''}
            placeholder={field.placeholder}
            onChange={(e) => handleInputChange(fieldKey, e.target.value)}
            onBlur={() => handleInputBlur(fieldKey)}
          />
        );
      case 'date':
        return (
          <TextInput
            ref={setTextInputRef}
            type="date"
            defaultValue={values[fieldKey] ?? ''}
            onChange={(e) => handleInputChange(fieldKey, e.target.value)}
            onBlur={() => handleInputBlur(fieldKey)}
          />
        );
      case 'select':
        return (
          <SelectInput
            value={values[fieldKey] ?? ''}
            onChange={(e) => onChange(fieldKey, e.target.value)}
          >
            <option value="" />
            {field.options?.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </SelectInput>
        );
      case 'multiselect':
        return (
          <SearchSelect
            options={field.options ?? []}
            value={values[fieldKey] ?? []}
            onChange={(v) => onChange(fieldKey, v)}
            variant="filter"
          />
        );
      case 'checkbox':
        if (field.checkboxLabel) {
          return (
            <CheckboxLabel>
              <WBSCheckbox
                checked={!!values[fieldKey]}
                onChange={(e) => onChange(fieldKey, e.target.checked)}
              />
              {field.checkboxLabel}
            </CheckboxLabel>
          );
        }
        return (
          <WBSCheckbox
            checked={!!values[fieldKey]}
            onChange={(e) => onChange(fieldKey, e.target.checked)}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Panel isOpen={isOpen} panelType="filter" maxHeight={maxHeight}>
      <PanelContent ref={contentRef}>
        <FilterGrid>
          {groupedFilters.map((filterGroup, _index) => {
            if (Array.isArray(filterGroup)) {
              // Side-by-side fields
              return (
                <FilterRow key={filterGroup.map((f) => f.key).join('-')}>
                  <DateRow>
                    {filterGroup.map((field) => (
                      <DateCol key={field.key}>
                        <Label>{field.label}</Label>
                        {renderFieldInput(field)}
                      </DateCol>
                    ))}
                  </DateRow>
                </FilterRow>
              );
            }
            // Single field
            const field = filterGroup;
            return (
              <FilterRow
                key={field.key}
                style={
                  field.colSpan ? { gridColumn: `span ${field.colSpan}` } : {}
                }
              >
                <Label>{field.label}</Label>
                {renderFieldInput(field)}
              </FilterRow>
            );
          })}
        </FilterGrid>

        <BottomRow>
          {!hideRememberFilterCheckbox ? (
            <CheckboxLabel>
              <WBSCheckbox
                checked={rememberChecked}
                onChange={(e) => onRememberChange?.(e.target.checked)}
              />
              {t('profile.remember-filter-inputs-and')}
            </CheckboxLabel>
          ) : (
            <span />
          )}
          <ButtonGroup>
            <ResetText type="button" onClick={handleResetClick}>
              {t('wbs.reset')}
            </ResetText>
            <WBSButton
              wide
              style={{ minWidth: 120 }}
              onClick={handleApplyClick}
            >
              {t('wbs.apply-filters')}
            </WBSButton>
          </ButtonGroup>
        </BottomRow>
      </PanelContent>
    </Panel>
  );
}

const TableFilterPanel = memo(TableFilterPanelComponent);
export default TableFilterPanel;

const BottomRow = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: space-between;
  margin-left: auto;
  margin-right: auto;
  margin-top: 2rem;
  max-width: 1000px;
`;

const ResetText = styled.button`
  align-items: center;
  background: none;
  border: none;
  border-radius: 4px;
  color: inherit;
  cursor: pointer;
  display: flex;
  font-family: var(--open-sans);
  font-size: 10px;
  font-weight: 400;
  height: 40px;
  letter-spacing: 0.6px;
  outline: none;
  padding: 0;
  transition: color 0.15s;
  &:hover,
  &:focus {
    color: var(--wbs-red);
  }
`;
